import { Button, Heading, Stack, Text } from '@chakra-ui/react'
import type { PublicJoinResponse, RegistrationStatus } from '@ipc/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'
import { api } from '~/api/client'
import { keys, useJoinInfo } from '~/api/hooks'
import { ActionBar } from '~/components/member/ActionBar'
import { FlowStepper } from '~/components/member/FlowStepper'
import { ErrorAlert, RetryError, useFriendlyError } from '~/components/member/feedback'
import { MemberLayout } from '~/components/member/layout'
import { WhyLink } from '~/components/member/WhyLink'
import { useT } from '~/i18n/use-t'
import { nextForStatus, pathForStatus } from '~/lib/flow'
import { useStepRegistration } from '~/lib/use-step'
import { StepSkeleton } from '~/components/member/StepSkeleton'
import LinkNotActive from '~/pages/join/LinkNotActive'
import { Consent, CONSENT_ID } from './Consent'
import { EmbeddedHost } from './embedded/EmbeddedHost'
import { FaceNotice } from './FaceNotice'
import { HostedHandoff } from './hosted/HostedHandoff'
import { CaptureScreen } from './own-capture/CaptureScreen'
import { uploadFrames } from './own-capture/upload'
import { Outcome } from './Outcome'
import { EMBEDDED } from './providers'
import { TestProviderBanner } from './TestProviderBanner'
import type { FaceDone } from './types'

/** The id of the face form: the Start button in the shell's action area submits it with `form=`. */
const FORM_ID = 'face-form'

/** What the face step knows about the member's tries. */
interface Tries {
  attempt: RegistrationStatus['attempt']
  attemptsLeft: number
  canStart: boolean
}

/**
 * T23 (copy v8 "One more picture", spec E12.5): the attempt's liveness is proven by the provider's own check and it
 * waits for our own still on the shared camera screen (`attempt.needsStill`). Not a new try; a picture the provider
 * cannot match to the check asks for another one.
 */
export function StillStep({ join, onDone }: { join: PublicJoinResponse; onDone: (done: FaceDone) => void }) {
  const { t } = useT()
  return (
    <Stack gap={5}>
      <TestProviderBanner join={join} />
      <Heading as='h2' size='xl'>
        {t('testProvider.stillTitle')}
      </Heading>
      <CaptureScreen
        providerId={join.providerId}
        title={t('testProvider.stillLine')}
        refusalText={{ still_not_matched: t('testProvider.stillNotMatched') }}
        requestToken={() => api('captureToken', { body: { consentBiometric: true } })}
        upload={uploadFrames}
        onDone={(registration) => onDone({ to: 'registration', registration })}
      />
    </Stack>
  )
}

/**
 * The body of S4 for any provider. It is chosen by `join.flow` (never by the provider's id): a hosted redirect, the
 * shared camera screen, or an embedded SDK component. `registration` is null when a voter (signed in) does
 * the check again after an administrator asked for it (R32).
 */
export function FaceStepBody({
  join,
  tries,
  onDone,
  member = false,
}: {
  join: PublicJoinResponse
  tries: Tries
  onDone: (done: FaceDone) => void
  /** A member does the check again (R32): provider handoffs return to the member pages. */
  member?: boolean
}) {
  const { t } = useT()
  const [consent, setConsent] = useState(false)
  const hintId = useId()
  const [begun, setBegun] = useState(false)
  const [stopped, setStopped] = useState(false)
  const outcomeKind = tries.attempt
    ? tries.attempt.status === 'start_failed'
      ? 'not_started'
      : tries.attempt.status === 'final'
        ? 'try_again'
        : null
    : null
  const [showStart, setShowStart] = useState(outcomeKind === null)
  const entry = join.flow === 'embedded_sdk' ? EMBEDDED[join.providerId] : undefined
  const requestToken = () => api('captureToken', { body: { consentBiometric: true } })

  useEffect(() => {
    void entry?.preload()
  }, [entry])

  const handle = (done: FaceDone) => {
    if (done.to === 'face') {
      setBegun(false)
      setConsent(false)
      setStopped(done.stopped === true)
      setShowStart(true)
      return
    }
    onDone(done)
  }

  if (!join.active || join.flow === null) return <Text role='alert'>{t('face.notActive')}</Text>
  // T23: a test provider's face-check screens show the staff-only banner first (copy v8, E12.9).
  const banner = <TestProviderBanner join={join} />
  const testProvider = join.publicSettings.testProvider === true

  if (!showStart && outcomeKind)
    return (
      <>
        {banner}
        <Outcome
          kind={outcomeKind}
          attemptsLeft={tries.attemptsLeft}
          canStart={tries.canStart}
          hints={tries.attempt?.outcomeHints ?? []}
          ourSide={tries.attempt?.ourSideFailure === true}
          onTryAgain={() => setShowStart(true)}
        />
      </>
    )

  if (begun) {
    if (join.flow === 'hosted_redirect') return <HostedHandoff />
    if (join.flow === 'own_capture')
      return (
        <CaptureScreen
          providerId={join.providerId}
          requestToken={requestToken}
          upload={uploadFrames}
          onDone={(registration) => handle({ to: 'registration', registration })}
        />
      )
    return (
      <>
        {banner}
        <EmbeddedHost join={join} onDone={handle} member={member} />
      </>
    )
  }

  // The consent box may be above or below the sticky bar, so the hint points to it and takes the member there.
  const focusConsent = () => {
    const box = document.getElementById(CONSENT_ID)
    box?.scrollIntoView({ block: 'center' })
    box?.focus()
  }
  const embedded = join.flow === 'embedded_sdk'
  const asksFlash = entry?.asksPhotosensitivity(join.publicSettings) ?? false
  return (
    <form
      id={FORM_ID}
      onSubmit={(e) => {
        e.preventDefault()
        if (consent) setBegun(true)
      }}
      noValidate
    >
      <Stack gap={2}>
        {banner}
        {stopped && <Text role='status'>{t('face.stopped')}</Text>}
        <Text>
          {t('face.intro')} <WhyLink faq='face' inline />
        </Text>
        {/* The legal floor (R0) stays on the page, in this order, before the consent that refers to it:
            the human review (Art. 22), no photo or video kept, the face template. */}
        <Text>{t('face.review')}</Text>
        <FaceNotice notice={join.faceNotice} provider={join.displayName} />
        <Consent provider={join.displayName} checked={consent} onChange={setConsent} testProvider={testProvider} />
        <Stack direction='row' gap={3} wrap='wrap' align='center'>
          <WhyLink faq='faceStart' label={t('face.whatNext')} />
          <Text aria-hidden color='fg.muted'>
            &middot;
          </Text>
          <WhyLink faq='cameraAllow' label={t('face.cameraHelpLink')} />
        </Stack>
        {/* The bar sits outside the scroller (spec §2.1): Start and why it is not available yet are always in reach
            while the member reads and ticks the box above. */}
        <ActionBar gap={1}>
          {/* One control is both the hint and the way to the box (a 48 px target, AC10); Start names it as its
              description while it is disabled. */}
          {!consent && (
            <WhyLink id={hintId} label={t('face.startHint')} onClick={focusConsent} alignSelf='flex-start' />
          )}
          <Button
            type='submit'
            form={FORM_ID}
            size='lg'
            w='full'
            disabled={!consent}
            aria-describedby={consent ? undefined : hintId}
          >
            {asksFlash ? t('embedded.continue') : testProvider ? t('testProvider.start') : t('face.start')}
          </Button>
        </ActionBar>
      </Stack>
    </form>
  )
}

/** `/join/:providerId/face` (S4). */
export default function FaceStep() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { providerId = '' } = useParams()
  const step = useStepRegistration(null, 'face', { backTo: `/join/${providerId}/place` })
  const join = useJoinInfo(step.reg?.provider ?? providerId)
  const [epoch, setEpoch] = useState(0)

  const frame = (children: React.ReactNode, backTo?: string) => (
    <MemberLayout flow backTo={backTo}>
      <FlowStepper step='face' />
      {children}
    </MemberLayout>
  )

  if (!step.ok) return step.gate
  if (join.isPending) return step.frame(<StepSkeleton />)
  if (join.isError) return step.frame(<RetryError error={join.error} onRetry={() => void join.refetch()} />)

  const status = step.reg
  const needsStill = status.attempt?.needsStill === true
  if (!needsStill && nextForStatus(status) !== 'face') return <Navigate to={pathForStatus(status)} replace />
  if (!join.data.active) return <LinkNotActive defaultJoinPath={join.data.defaultJoinPath} />

  const handle = (done: FaceDone) => {
    if (done.to === 'wait') return navigate(`/join/${status.provider}/wait`)
    if (done.to === 'registration') {
      setEpoch((e) => e + 1)
      qc.setQueryData(keys.registration, done.registration)
      return navigate(pathForStatus(done.registration))
    }
    if (done.to === 'switch') {
      void qc.invalidateQueries({ queryKey: keys.registration })
      return navigate(`${done.joinPath}/face`)
    }
  }

  if (needsStill) return frame(<StillStep key={`still-${epoch}`} join={join.data} onDone={handle} />)

  return frame(
    <FaceStepBody
      key={`${status.attempt?.id ?? 'first'}-${epoch}`}
      join={join.data}
      tries={{ attempt: status.attempt, attemptsLeft: status.attemptsLeft, canStart: status.canStart }}
      onDone={handle}
    />,
    `/join/${status.provider}/place`
  )
}
